<script lang="ts" setup>
import { SancareBreadcrumb, SancareOcticon, SancareSticky } from '@sancare/typescript-commons'
import { DrugEventPrestationType, HealthDataDocument, HealthDataType } from '@sancare/typescript-commons/src/types/health-data'
import _ from 'lodash'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import { useBreadcrumb } from '@/breadcrumbs/useBreadcrumb'
import LineClampToggle from '@/common/LineClampToggle.vue'
import ScrollToTopButton from '@/common/ScrollToTopButton.vue'
import { keepRumEvaluations } from '@/misc/stayJustifications'
import { getSelectedChunks } from '@/stay-displayer/chunkJustifications'
import { useAutomationPreview } from '@/stay-displayer/composables/useAutomationPreview'
import MedicalActList from '@/stay-displayer/mco/medical-act/MedicalActList.vue'
import { useStore } from '@/store'
import { CountryEnum, PmsiTypeEnum } from '@/store/modules/settings/types'
import { ModeEnum } from '@/store/modules/settings/types'
import { formattedHealthData } from '@/store/modules/stay/types'

import AutomationDiagnosisGroupPreview from './AutomationDiagnosisGroupPreview.vue'
import DiagnosisListAutomationDemoMode from './DiagnosisListAutomationDemoMode.vue'
import DiagnosisListAutomationMode from './DiagnosisListAutomationMode.vue'
import DiagnosisListStandardMode from './DiagnosisListStandardMode.vue'
import DocumentViewer from './DocumentViewer.vue'
import GroupingActions from './grouping/mco/GroupingActions.vue'
import GroupingCardCh from './grouping/mco/GroupingCardCh.vue'
import GroupingCardFr from './grouping/mco/GroupingCardFr.vue'
import KeywordButton from './KeywordButton.vue'
import GeneralStayData from './mco/GeneralStayData.vue'
import ReadingListNavigator from './ReadingListNavigator.vue'
import ReconductedFromBanner from './ReconductedFromBanner.vue'
import ReportSelector from './ReportSelector.vue'
import RumSelector from './RumSelector.vue'
import StayComment from './StayComment.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const props = defineProps<{
  stayId: string
  idType: string
}>()

const { breadcrumbItems } = useBreadcrumb()

// Canonicalize the URL: sync ?stayLabel=... with the stay's administrativeLocalStayId
// so the breadcrumb can render the right number without reading the store.
watch(() => store.state.stayDisplayer.displayedStay?.administrativeLocalStayId, (stayLabel) => {
  if (stayLabel !== null && stayLabel !== undefined && String(route.query.stayLabel) !== String(stayLabel)) {
    router.replace({ query: { ...route.query, stayLabel: String(stayLabel) } })
  }
}, { immediate: true })

const displayedRumId = ref(0)
const selection = ref(null)
const displayAllKeywords = ref(false)
const displayKeywordsToggleButton = ref(true)

const flatAppSettings = computed(() => store.getters.flatAppSettings)
const flatSancareSettings = computed(() => store.getters.flatSancareSettings)
const healthData = computed(() => formattedHealthData(store.state.stayDisplayer.displayedStay))
const displayedStay = computed(() => store.state.stayDisplayer.displayedStay)
// Derived purely from the search in progress (kw URL param), independently of which stay is
// displayed — not part of the stay itself.
const additionnalKeywordSearches = computed(() => store.getters.additionnalKeywordSearches)
// True while the documents/justifications/suggestions fetch + merge is in flight for the displayed
// stay — request state, not part of the stay itself.
const isRefreshingStayContent = computed(() => store.state.stayDisplayer.isRefreshingStayContent)
const mode = computed(() => (store.state.route.query.mode ? store.state.route.query.mode : ModeEnum.OPTIMIZABLE))
const automationGroupId = computed(() => (store.state.route.query.automationGroupId ? Number(store.state.route.query.automationGroupId) : null))
// Set alongside automationGroupId when navigating from an automation group's stay list (see
// AutomationGroupThumbnail.vue::gotoGroupStayList) — lets us pick the right preview endpoint without
// an extra fetch. Absent when reached without a specific group (e.g. the general dashboard tab).
const automationGroupType = computed(() => store.state.route.query.automationType ?? null)
const teamMode = computed(() => store.state.login.currentUser.teamMode)
const pmsiCountry = computed(() => store.state.settings.pmsi.country)
const scrollTarget = computed(() => store.state.stayDisplayer.scrollTarget)
const mainContent = computed(() => store.state.stayDisplayer.mainContent)
const getStayRequest = computed(() => store.state.stayDisplayer.getStayRequest)

// Every suggestion type (search-matched, keyword-matched, or AI-predicted) comes from the
// backend-resolved suggestionJustifications/keywordJustifications (see
// chunkJustifications.ts::getSelectedChunks).
const selectedChunks = computed(() => {
  if (!selection.value) {
    return null
  }
  const stay = store.state.stayDisplayer.displayedStay

  const keywordId = selection.value.savedSearchIds.length === 1 ? selection.value.savedSearchIds[0] : null
  const justification = (keywordId !== null ? stay.keywordJustifications?.[keywordId] : null)
    ?? stay.suggestionJustifications?.[`${selection.value.labelType}:${selection.value.labelReference}`]
    ?? null
  if (!justification) {
    return null
  }

  const documentIdByGeneratedDataId = new Map<string, number>([
    ...[...stay.reports, ...stay.textualHealthEntries]
      .filter((document) => Boolean(document.generatedDataId))
      .map((document): [string, number] => [document.generatedDataId, document.id]),
  ])

  return getSelectedChunks(justification, documentIdByGeneratedDataId)
})

const hasCurrentCoding = computed(() => displayedStay.value?.rums.some((rum) => rum.standardModeDiagnosisGroup.currentDP) ?? false)
const isAutomationMode = computed(() => mode.value === ModeEnum.AUTOMATION)
const isValidated = computed(() => displayedStay.value?.status === 'validated')
const displayedRum = computed(() => {
  if (displayedRumId.value === null) {
    return displayedStay.value.rums[0]
  }
  if (displayedRumId.value === 0 && displayedStay.value.rssRum) {
    return displayedStay.value.rssRum
  }
  return displayedStay.value.rums.find((rum) => rum.id === displayedRumId.value) || displayedStay.value.rums[0]
})
const displayedRumIdx = computed(() => displayedStay.value.rums.findIndex((rum) => rum.id === displayedRumId.value))
const displayedRumSuggestionJustifications = computed(() => displayedStay.value.suggestionJustifications
  ? keepRumEvaluations(displayedStay.value.suggestionJustifications, displayedRum.value.id)
  : null)

const {
  rulesAutomationPreview,
  aiAutomationPreview,
  currentRumDiagnosisGroupPreview,
  currentRumAiDiagnosisGroupPreview,
  automationMajorRumId,
} = useAutomationPreview({
  displayedStay,
  displayedRumId,
  currentRumId: computed(() => displayedRum.value.id),
  isAutomationMode,
  automationGroupId,
  automationGroupType,
  hasCurrentCoding,
  isDemoMode: computed(() => flatSancareSettings.value['demo_mode']),
})

const reconductedFromStayLabel = computed(() => (
  isValidated.value
    ? (displayedStay.value?.reconductedFromStay ?? null)
    : (rulesAutomationPreview.value?.previousStay?.administrativeLocalStayId ?? null)
))
const automationAsideGhmGroupingInfo = computed(() => {
  // In a rules automation context, we simulate the groupage
  if (rulesAutomationPreview.value) {
    return {
      ghm: rulesAutomationPreview.value.ghm,
      ghs: rulesAutomationPreview.value.ghs,
      value: rulesAutomationPreview.value.value,
      groupingError: rulesAutomationPreview.value.groupingError,
    }
  }
  if (!hasCurrentCoding.value) {
    // AI automation mode : already precomputed at import time, served by the dedicated preview
    // endpoint (getAiAutomationPreview) instead of the raw stay fetch.
    const preview = aiAutomationPreview.value
    return {
      ghm: preview?.ghm ?? null,
      ghs: preview?.ghs ?? null,
      value: preview?.value ?? 0,
      groupingError: preview?.groupingError ?? null,
    }
  }
  return {
    ghm: displayedStay.value.standardModeGhm,
    ghs: displayedStay.value.standardModeGhs,
    value: displayedStay.value.standardModeValue,
    groupingError: displayedStay.value.standardModeGroupingError,
  }
})
// True when the grouping/diagnoses shown are a simulated automation proposal rather than the stay's actual current coding.
const isGroupingProposal = computed(() => Boolean(rulesAutomationPreview.value) || !hasCurrentCoding.value)
const isAutomationDiagnosisProposal = computed(() => !displayedRum.value.standardModeDiagnosisGroup.currentDP)
const mostRecentHealthUpdate = computed(() => {
  const max = _.maxBy(
    [
      { type: 'healthData', label: '[Ajout de données de santé] : ', date: displayedStay.value.lastHealthDataUpdate },
      { type: 'prediction', label: '[Ajout de détections] : ', date: displayedStay.value.lastPredictionUpdate },
      { type: 'external', label: '[Connecteur PMSI] : ', date: displayedStay.value.lastExternalPmsiUpdate }
    ],
    'date')
  return `${max.label} le ${max.date.format('DD/MM/YYYY')} à ${max.date.format('HH:mm')}`
})
const hasGroupingError = computed(() => {
  const ghm = isAutomationMode.value
    ? automationAsideGhmGroupingInfo.value.ghm
    : (displayedStay.value.standardModeGhm ?? displayedStay.value.firstStandardModeGhm)
  return Boolean(ghm?.reference.startsWith('90Z'))
})
onBeforeRouteLeave((_to, _from, next) => {
  store.commit('resetReadingList')
  next()
})

watch(() => displayedStay.value?.id, (newId) => {
  if (!newId) return

  preselectRum()
  selection.value = null
  setMainContent(null)
  preselectJustificationTarget()

  store.commit('updateStayStatuses', {
    stayId: displayedStay.value.id,
    username: store.state.login.username
  })
}, { immediate: true })

// getStay resolves the stay itself before the documents (reports' content) finish merging in (see
// getStay/refreshDisplayedMcoStay in stay-displayer/store.js) - the watcher above can therefore run
// preselectJustificationTarget() before the targeted report's content is available, in which case its
// report-existence guard silently no-ops. Retry once the merge completes, unless the user has since
// opened something themselves.
watch(isRefreshingStayContent, (refreshing, wasRefreshing) => {
  if (wasRefreshing && !refreshing && mainContent.value === null) {
    preselectJustificationTarget()
  }
})

watch(() => props.stayId, (newStayId) => {
  if (!newStayId) {
    return
  }
  store.dispatch(
    'getStay',
    {
      stayId: newStayId,
      idType: props.idType,
    }
  )
})

watch(displayedRumId, () => {
  selection.value = null
})

watch(teamMode, (prev) => {
  if (!_.isNull(prev)) {
    refresh()
  }
})

onMounted(() => {
  refresh()
  preselectRum()
  preselectJustificationTarget()
})

function selectRum(rumId) {
  displayedRumId.value = rumId
}

function makeSelection({ labelType, labelReference, savedSearchIds }) {
  if (selection.value && labelType === selection.value.labelType && labelReference === selection.value.labelReference && _.isEqual(savedSearchIds, selection.value.savedSearchIds)) {
    selection.value = null
  } else {
    selection.value = { labelType, labelReference, savedSearchIds: savedSearchIds }
  }
}

// AI automation mode's DP/DR candidate list, sourced from the dedicated backend preview
// (getAiAutomationPreview) — these don't carry chunks (no source-text highlighting), which is
// fine here since only DiagnosisListAutomationMode/DemoMode.vue consume this, to build the DP/DR
// candidate cards, not the document viewer.
function getAiRumPreview() {
  const preview = aiAutomationPreview.value
  return preview?.rumDiagnosisGroups.find((group) => group.rumId === displayedRumId.value) ?? null
}

function getAutomationCandidateDiagnoses() {
  const rumPreview = getAiRumPreview()
  if (!rumPreview) {
    return []
  }
  const toPredictedLabel = (type, predicted) => ({
    type,
    diagnosis: { reference: predicted.reference, description: predicted.description },
    confidence: predicted.confidence,
    automationModeExcluded: predicted.excluded,
    hasRational: predicted.hasRational,
  })
  return [
    ...rumPreview.dp.map((predicted) => toPredictedLabel('DP', predicted)),
    ...rumPreview.dr.map((predicted) => toPredictedLabel('DR', predicted)),
  ]
}

// The DP/DR already resolved at import time (as opposed to the raw candidates above) — lets
// DiagnosisListAutomationMode/DemoMode.vue tell "currently applied" from "alternative", same shape
// as rum.automationModeDiagnosisGroup which this replaces as a data source.
function getAutomationPreviewDiagnosisGroup() {
  const preview = aiAutomationPreview.value
  if (!preview) {
    return { currentDP: null, currentDR: null }
  }
  // Vue RSS (agrégat) : comme rssRumBuilder.js, le DP/DR courant vient du RUM majeur (isMajorRum
  // renvoyé par le preview), pas d'un RUM d'id 0 (qui n'existe pas dans la réponse, un RUM par RUM
  // réel du séjour).
  const targetRumId = displayedRumId.value === 0
    ? (preview.rumDiagnosisGroups.find((group) => group.isMajorRum)?.rumId ?? null)
    : displayedRumId.value
  const rumPreview = preview.rumDiagnosisGroups.find((group) => group.rumId === targetRumId)
  return {
    currentDP: rumPreview?.currentDp ?? null,
    currentDR: rumPreview?.currentDr ?? null,
  }
}

function disableJustificationScrolling() {
  store.commit('setScrollTarget', null)
}

function setMainContent(mainContent: HealthDataDocument | null) {
  store.commit('setMainContent', mainContent)
}

function refresh() {
  if (props.stayId) {
    store.dispatch(
      'getStay',
      {
        stayId: props.stayId,
        idType: props.idType,
      }
    )
  }
}

function preselectRum() {
  if (!displayedStay.value) {
    return
  }

  const queryExternalRumId = displayedStay.value.rums.find(
    (rum) => rum.externalPmsiId && (rum.externalPmsiId.toString() === route.query.externalRumId)
  )?.id

  const queryGuessRumId = displayedStay.value.rums.find(
    (rum) => (rum.rumStart.format('DDMMYY') + rum.medicalUnit?.reference).toUpperCase() === route.query.externalRumId
  )?.id

  const queryRumId = displayedStay.value.rums.find(
    (rum) => rum.id.toString() === route.query.rumId
  )?.id

  displayedRumId.value = queryGuessRumId ?? queryExternalRumId ?? queryRumId ?? displayedStay.value.rums[0].id
}

// Mirrors preselectRum: applies a target set from outside the page (the aside's justification popin,
// which has no document viewer — see StayQuickView.vue's viewJustificationTarget) via the same
// setMainContent/setScrollTarget a click here would trigger. docType is the full HealthDataDocument.type.
function preselectJustificationTarget() {
  if (!displayedStay.value) {
    return
  }

  const docType = route.query.docType
  if (typeof docType !== 'string') {
    return
  }

  const docId = typeof route.query.docId === 'string' ? Number(route.query.docId) : null
  // ReportDisplayer.vue requires a matching report and dereferences it unconditionally (report.id
  // etc.) - a stale or tampered docId for a 'report' docType would otherwise crash DocumentViewer.vue
  // instead of just showing nothing, unlike every other docType which already degrades gracefully.
  if (docType === HealthDataType.REPORT && !healthData.value.reports.some((report) => report.id === docId)) {
    return
  }

  const drugEventPrestationType = typeof route.query.drugEventPrestationType === 'string'
    ? route.query.drugEventPrestationType as DrugEventPrestationType
    : undefined

  setMainContent({ type: docType, targetId: docId, drugEventPrestationType })

  const wordOffset = route.query.wordOffset
  if (docId !== null && typeof wordOffset === 'string') {
    store.commit('setScrollTarget', { docId, offset: Number(wordOffset) })
  }
}

async function comment(comment, id) {
  await store.dispatch(
    'commentStay',
    {
      stayId: id,
      comment,
      pmsiType: store.getters.pmsiType
    }
  )
}
</script>

<template>
  <div class="container-fluid ps-4 pe-0">
    <div v-if="displayedStay && getStayRequest.ok">
      <div>
        <sancare-breadcrumb :items="breadcrumbItems" />
        <reading-list-navigator
          :mode="mode"
          :current-stay-id="Number(stayId)"
          :pmsi-type="PmsiTypeEnum.MCO"
        />
      </div>
      <div class="d-flex">
        <div class="flex-grow-1 row g-0">
          <div
            style="margin-bottom: 45px;"
            class="col-7 p-2 pe-4"
          >
            <general-stay-data
              :stay="displayedStay"
              :displayed-rum-id="displayedRumId"
            />
            <section v-if="additionnalKeywordSearches.length">
              <div class="row">
                <div class="col">
                  <span> {{ `${t('stayDisplayer.mco.keywords')} (${ additionnalKeywordSearches.length })` }}</span>
                </div>
              </div>
              <line-clamp-toggle
                :line-clamp="3"
                :is-open="displayAllKeywords"
                @display-toggle-button="displayToggle => displayKeywordsToggleButton = displayToggle"
              >
                <keyword-button
                  v-for="(keyword) in additionnalKeywordSearches"
                  :key="keyword.search.id"
                  :keyword-value="keyword.value"
                  :document-type="keyword.documentType"
                  :keyword-index="keyword.search.id"
                  :is-highlighted="selection && selection.savedSearchIds.indexOf(keyword.search.id) !== -1"
                  :health-data="healthData"
                  :is-loading="isRefreshingStayContent"
                  :keyword-justifications="displayedStay.keywordJustifications"
                  @make-selection="makeSelection"
                  @disable-justification-scrolling="disableJustificationScrolling"
                />
              </line-clamp-toggle>
              <div
                v-if="displayKeywordsToggleButton"
                class="text-end"
              >
                <span
                  class="btn btn-sm btn-link"
                  @click="displayAllKeywords = !displayAllKeywords"
                >
                  <sancare-octicon
                    :name="displayAllKeywords ? 'chevron-up' : 'chevron-down'"
                    :height="12"
                    style="fill: currentcolor;"
                  />
                  {{ displayAllKeywords ? 'réduire les mots-clés' : 'afficher tous les mots-clés' }}
                </span>
              </div>
            </section>
            <div
              v-if="isRefreshingStayContent"
              class="mt-4"
            >
              <div class="loader loader-center mb-4" />
              <p>{{ t('stayDisplayer.loadingDocuments') }}</p>
            </div>
            <report-selector
              v-else-if="mainContent === null"
              :health-data="healthData"
              :selected-chunks="selectedChunks"
              :main-content="mainContent"
              @set-main-content="setMainContent"
            />
            <div
              v-else
              class="mt-4"
            >
              <button
                class="btn btn-link btn-sm px-0 mb-2"
                @click="setMainContent(null)"
              >
                &larr;&nbsp;&nbsp;{{ t('stayDisplayer.mco.backToDocumentList') }}
              </button>
              <document-viewer
                :current-stay="displayedStay"
                :current-document="mainContent"
                :selection="selection"
                :selected-chunks="selectedChunks"
                :scroll-target="scrollTarget"
                @disable-justification-scrolling="disableJustificationScrolling()"
              />
            </div>
          </div>
          <sancare-sticky
            :custom-styles="{'padding-bottom': '45px'}"
            class="col-5 p-2"
          >
            <div class="row g-0 mb-4">
              <grouping-card-ch
                v-if="pmsiCountry === CountryEnum.CH"
                :cflag="displayedStay.standardModeDrg ? displayedStay.standardCflag : displayedStay.firstStandardCflag"
                :drg="displayedStay.standardModeDrg ?? displayedStay.firstStandardModeDrg ?? {reference:'E64C', description: 'Insuffisance respiratoire ou embolie pulmonaire, plus d\'un jour d\'hospitalisation'}"
                :ecw="displayedStay.standardModeDrg ? displayedStay.standardModeValue : displayedStay.firstStandardModeValue"
                :grouping-error="displayedStay.standardModeGroupingError"
                :pccl="displayedStay.standardModeDrg ? displayedStay.standardPccl : displayedStay.firstStandardPccl"
                class="col-12"
              >
                <template #actions>
                  <grouping-actions
                    :stay="displayedStay"
                    :can-validate="!displayedStay.standardModeGroupingError"
                  />
                </template>
              </grouping-card-ch>
              <grouping-card-fr
                v-else-if="!isAutomationMode"
                :mode="mode"
                :ghm="displayedStay.standardModeGhm ? displayedStay.standardModeGhm : displayedStay.firstStandardModeGhm"
                :ghs="displayedStay.standardModeGhm ? displayedStay.standardModeGhs : displayedStay.firstStandardModeGhs"
                :value="displayedStay.standardModeGhm ? displayedStay.standardModeValue : displayedStay.firstStandardModeValue"
                :money-gap="displayedStay.standardModeGhm ? (displayedStay.standardModeValue - displayedStay.firstStandardModeValue) : 0"
                :grouping-error="displayedStay.standardModeGroupingError"
                class="col-12"
              >
                <template #actions>
                  <grouping-actions
                    :stay="displayedStay"
                    :can-validate="!hasGroupingError"
                  />
                </template>
              </grouping-card-fr>
              <grouping-card-fr
                v-else
                :mode="mode"
                :is-proposal="isGroupingProposal"
                v-bind="automationAsideGhmGroupingInfo"
                class="col-12"
              >
                <template #actions>
                  <grouping-actions
                    :stay="displayedStay"
                    :can-validate="!hasGroupingError"
                    :automation-group-id="automationGroupId"
                    is-automation
                    :is-demo-mode="flatSancareSettings['demo_mode']"
                  />
                </template>
              </grouping-card-fr>
            </div>
            <div class="row g-0 mb-4">
              <span
                v-tooltip="mostRecentHealthUpdate"
                class="icon-blue icon-blue-label d-flex flex-row gap-2 align-items-center justify-content-start mt-2 col-5"
              >
                <sancare-octicon
                  name="stopwatch"
                  :width="20"
                  :height="20"
                />
                <span>
                  {{ t('stayDisplayer.mco.lastUpdate') }}
                </span>
              </span>
            </div>
            <diagnosis-list-standard-mode
              v-if="!isAutomationMode"
              :sancare-settings="flatSancareSettings"
              :app-settings="flatAppSettings"
              :mode="mode"
              :ghm="displayedStay.firstStandardModeGhm"
              :rum-id="displayedRum.id"
              :standard-mode-diagnosis-group="displayedRum.standardModeDiagnosisGroup"
              :selection="selection"
              :is-loading="isRefreshingStayContent"
              :health-data="healthData"
              :stay-suggestions="displayedStay.staySuggestions"
              :standard-mode-excluded-diagnoses="displayedStay.standardModeExcludedDiagnoses"
              :suggestion-justifications="displayedRumSuggestionJustifications"
              :displayed-rum-idx="displayedRumIdx"
              :stay-has-initial-coding="displayedStay.hasInitialCoding"
              @make-selection="makeSelection"
            />
            <reconducted-from-banner
              v-if="isAutomationMode && reconductedFromStayLabel"
              :administrative-local-stay-id="reconductedFromStayLabel"
            />
            <automation-diagnosis-group-preview
              v-if="isAutomationMode && !flatSancareSettings['demo_mode'] && (currentRumDiagnosisGroupPreview || currentRumAiDiagnosisGroupPreview)"
              :diagnosis-group="currentRumDiagnosisGroupPreview ?? currentRumAiDiagnosisGroupPreview"
            />
            <diagnosis-list-automation-mode
              v-else-if="isAutomationMode && !flatSancareSettings['demo_mode']"
              :app-settings="flatAppSettings"
              :rum-id="displayedRum.id"
              :diagnosis-group="displayedRum.standardModeDiagnosisGroup.currentDP ? displayedRum.standardModeDiagnosisGroup : getAutomationPreviewDiagnosisGroup()"
              :is-proposal="isAutomationDiagnosisProposal"
              :selection="selection"
              :is-loading="isRefreshingStayContent"
              :health-data="healthData"
              :rum-predicted-labels="getAutomationCandidateDiagnoses()"
              :suggestion-justifications="displayedRumSuggestionJustifications"
              @make-selection="makeSelection"
            />
            <diagnosis-list-automation-demo-mode
              v-if="isAutomationMode && flatSancareSettings['demo_mode']"
              :app-settings="flatAppSettings"
              :rum-id="displayedRum.id"
              :standard-mode-diagnosis-group="displayedRum.standardModeDiagnosisGroup"
              :automation-preview-diagnosis-group="getAutomationPreviewDiagnosisGroup()"
              :automation-rules-preview="currentRumDiagnosisGroupPreview"
              :selection="selection"
              :is-loading="isRefreshingStayContent"
              :health-data="healthData"
              :rum-predicted-labels="getAutomationCandidateDiagnoses()"
              :suggestion-justifications="displayedRumSuggestionJustifications"
              @make-selection="makeSelection"
            />
            <stay-comment
              :stay="displayedStay"
              class="mb-3"
              @comment-stay="comment"
            />
            <medical-act-list
              :coded-medical-acts="displayedRum.codedMedicalActs"
              :health-data="healthData"
              :pmsi-country="pmsiCountry"
              :rum="displayedRum"
              :suggestion-justifications="displayedRumSuggestionJustifications"
              :stay-suggestions="displayedStay.staySuggestions"
              @make-selection="makeSelection"
            />
          </sancare-sticky>
        </div>
        <div class="ps-2">
          <rum-selector
            v-if="pmsiCountry !== CountryEnum.CH"
            :mode="mode"
            :rums="displayedStay.rums"
            :displayed-rum-id="displayedRumId"
            :automation-major-rum-id="automationMajorRumId"
            @select-rum="selectRum"
          />
        </div>
      </div>
      <scroll-to-top-button />
    </div>
    <div
      v-else-if="getStayRequest.error"
      class="row align-items-start"
    >
      <div
        v-if="getStayRequest.error.status === 404"
        class="stay-page-error-message col-auto mx-auto"
      >
        <h1>{{ t('stayDisplayer.stayError.notFoundTitle', { stayId }) }}</h1>
        <div>{{ t('stayDisplayer.stayError.causesIntro') }}</div>
        <ul>
          <li>{{ t('stayDisplayer.stayError.causeWrongId') }}</li>
          <li>{{ t('stayDisplayer.stayError.causeNotImported') }}</li>
        </ul>
      </div>
      <div
        v-else
        class="stay-page-error-message col-auto mx-auto"
      >
        <h1>{{ t('stayDisplayer.stayError.genericTitle') }}</h1>
        <div>{{ t('stayDisplayer.stayError.genericBody') }}</div>
        <div>{{ t('stayDisplayer.stayError.genericHelp') }}</div>
        <!-- TODO Mettre le modal de rapport de bug directement ici -->
      </div>
    </div>
  </div>
</template>
